import { useState, useCallback, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, type FieldValues, type FieldPath } from 'react-hook-form';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import { InputBaseComponentProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Classnames } from '@cbridge-utils/mui-theme';
import { FieldControlInputContainer } from '../_components/FieldControlInputContainer/FieldControlInputContainer';
import { FieldControlLabelContainer } from '../_components/FieldControlLabelContainer/FieldControlLabelContainer';
import { PasswordAdornment } from '../_components/PasswordAdornment/PasswordAdornment';
import { ClearButtonAdornment } from '../_components/ClearButtonAdornment/ClearButtonAdornment';
import { getPasswordInputProps, getPasswordInputType, maskedInputStyle } from '../_helpers/passwordInput';
import { ITextFieldControlProps } from './TextFieldControl.interface';

// A clearable field reveals its clear button on hover/focus (always on touch
// devices), the way the search field and the clearable MUI pickers do.
// A hidden password is a text input masked with CSS, see `passwordInput.ts`.
const StyledInput = styled(Input, {
  shouldForwardProp: (prop) => prop !== 'revealClearOnHover' && prop !== 'maskText',
})<{ revealClearOnHover: boolean; maskText: boolean }>(({ revealClearOnHover, maskText }) => ({
  ...(revealClearOnHover && {
    '@media (pointer: fine)': {
      [`& .${Classnames.VISIBLE_ON_HOVER}`]: {
        opacity: 0,
        transition: 'opacity 0.3s linear',
      },
      [`&:hover .${Classnames.VISIBLE_ON_HOVER}, &:focus-within .${Classnames.VISIBLE_ON_HOVER}`]: {
        opacity: 1,
      },
    },
  }),
  ...(maskText && maskedInputStyle),
}));

export const TextFieldControl = <T extends FieldValues, TName extends FieldPath<T>>(props: ITextFieldControlProps<TName>) => {
  const {
    name,
    label,
    tooltip,
    action,
    type,
    readOnly,
    required,
    postfix,
    placeholder,
    mixedValues,
    bulkEdit,
    thousandSeparator,
    clearable,
    revertValue,
  } = props;
  const { t } = useTranslation();
  const isPasswordField = type === 'password';

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const {
    field: {
      disabled, value, onChange, onBlur, ref, ...restFieldProps
    },
    fieldState: { invalid, error },
  } = useController<T, TName>({
    name,
    disabled: readOnly,
  });

  // A revealed password is hidden again when the form switches between edit
  // and view mode, so view mode never shows it and a fresh edit starts masked.
  const [wasDisabled, setWasDisabled] = useState(disabled);
  if (wasDisabled !== disabled) {
    setWasDisabled(disabled);
    setIsPasswordVisible(false);
  }

  const handleClickShowPassword = () => setIsPasswordVisible((show) => !show);
  const passwordAdornment = isPasswordField && !disabled && (
    <PasswordAdornment
      isPasswordVisible={isPasswordVisible}
      togglePasswordVisibility={handleClickShowPassword}
    />
  );

  const postfixAdornment = postfix && (
    <InputAdornment
      position={'end'}
      disableTypography={true}
      sx={{
        marginRight: 1,
      }}
    >
      {postfix}
    </InputAdornment>
  );

  // In read-only mode the action (e.g. a link button) is rendered inside the
  // input as an adornment, so the field can take up the full width. In edit
  // mode it stays outside, because inputs may have built-in adornments.
  const showActionInside = disabled && Boolean(action);
  const actionAdornment = showActionInside && (
    <InputAdornment position={'end'}>{action}</InputAdornment>
  );
  const actualType = (() => {
    if (isPasswordField) {
      return getPasswordInputType(isPasswordVisible);
    }
    return type === 'integer' || type === 'number' ? 'text' : type;
  })();

  // integer
  const handleIntegerInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    event.target.value = inputValue.replace(/[^\d-]|(?<=.)-/g, '');
    onChange(event);
  }, [onChange]);

  // number (float)
  const handleNumberInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;
    inputValue = inputValue
      .replace(/,/g, '.') // replace comma with dot
      .replace(/[^0-9.-]/g, '') // only number symbols
      .replace(/(?!^)-/g, '') // filter minus before number
      .replace(/(\..*)\./g, '$1') // filter double dot
      .replace(/^\./, '') // filter dot at the beginning
      .replace(/^-\./, '-'); // filter dot after minus

    event.target.value = inputValue;
    onChange(event);
  }, [onChange]);

  // string
  const handleStringInputChange = onChange;

  const getChangeHandler = () => {
    switch (type) {
      case 'integer':
        return handleIntegerInputChange;
      case 'number':
        return handleNumberInputChange;
      default:
        return handleStringInputChange;
    }
  };

  const inputProps: InputBaseComponentProps = {
    inputMode: type === 'integer' || type === 'number'
      ? 'numeric'
      : 'text',
    ...(isPasswordField && getPasswordInputProps(isPasswordVisible)),
  };

  const isValueEmpty = value === undefined || value === null || value === '';
  const showMixedPlaceholder = Boolean(mixedValues) && isValueEmpty;
  const resolvedPlaceholder = showMixedPlaceholder ? t('text:common.bulkEdit.mixedValues') : placeholder;

  const handleClear = useCallback(() => onChange(''), [onChange]);
  const clearAdornment = clearable && !disabled && !isValueEmpty && (
    <ClearButtonAdornment isSearch={false} onClick={handleClear} edge={false} />
  );

  const hasEndAdornment = Boolean(passwordAdornment || clearAdornment || postfixAdornment || actionAdornment);
  const endAdornment = hasEndAdornment ? (
    <>
      {passwordAdornment}
      {clearAdornment}
      {postfixAdornment}
      {actionAdornment}
    </>
  ) : undefined;

  const isThousandFormatted = thousandSeparator && type === 'integer';
  const displayedValue = (() => {
    if (isValueEmpty) {
      return '';
    }
    if (isThousandFormatted && !isFocused) {
      const num = Number(value);
      return Number.isFinite(num) ? num.toLocaleString('en-US') : String(value);
    }
    return value;
  })();

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => {
    setIsFocused(false);
    onBlur();
  };

  return (
    <FieldControlLabelContainer
      label={label}
      tooltip={tooltip}
      htmlFor={name}
      disabled={disabled}
      error={invalid}
      required={required}
    >
      <FieldControlInputContainer
        name={name}
        errorMessage={error?.message}
        action={showActionInside ? undefined : action}
        bulkEdit={bulkEdit}
        revertValue={revertValue}
      >
        <StyledInput
          {...restFieldProps}
          inputRef={ref}
          value={displayedValue}
          onChange={getChangeHandler()}
          onFocus={handleFocus}
          onBlur={handleBlur}
          id={name}
          disabled={disabled}
          error={invalid}
          fullWidth={true}
          type={actualType}
          placeholder={resolvedPlaceholder}
          endAdornment={endAdornment}
          inputProps={inputProps}
          revealClearOnHover={Boolean(clearable)}
          maskText={isPasswordField && !isPasswordVisible}
          sx={showMixedPlaceholder ? {
            '& input::placeholder': {
              fontStyle: 'italic',
              color: '#00897B',
              opacity: 1,
            },
          } : undefined}
        />
      </FieldControlInputContainer>
    </FieldControlLabelContainer>
  );
};
